import { useState } from "react";

import {
  lookups as lookupsApi,
  organizations as organizationsApi,
  questionLibrary,
} from "../api/resources";
import { API_BASE_URL } from "../api/client";
import { EnterpriseContextCard } from "../components/EnterpriseContextCard";
import { Card, EmptyState, ErrorBanner, Field, Loading, PageHeader, Stat } from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { useCurrentOrganization, useOrganizations } from "../lib/OrganizationContext";
import { useAsync } from "../lib/useAsync";

export function SettingsPage() {
  const { organization, organizationId } = useCurrentOrganization();
  const { refresh } = useOrganizations();
  const { can } = useAuth();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [form, setForm] = useState<Record<string, string>>({});

  const capabilities = useAsync(() => lookupsApi.capabilities(), []);
  const validation = useAsync(() => questionLibrary.validate(), []);
  const lookups = useAsync(() => lookupsApi.all(), []);

  if (!organizationId || !organization) {
    return (
      <>
        <PageHeader title="Settings" />
        <Card>
          <EmptyState title="No organization selected" />
        </Card>
      </>
    );
  }

  const value = (key: keyof typeof organization) =>
    form[key as string] ?? (organization[key] as string | null) ?? "";

  async function save() {
    setError(undefined);
    try {
      await organizationsApi.update(organizationId!, form);
      setSaved(true);
      setForm({});
      refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <>
      <PageHeader
        title="Settings"
        description="Organization details, server capabilities and the health of the question library."
      />

      <div className="stack">
        <ErrorBanner error={error} />
        {saved ? <div className="banner banner--ok">Organization updated.</div> : null}

        <Card
          title="Organization"
          actions={
            <button
              className="primary"
              disabled={Object.keys(form).length === 0}
              onClick={() => void save()}
            >
              Save changes
            </button>
          }
        >
          <div className="grid grid--two">
            {(
              [
                ["legal_name", "Legal name"],
                ["dba_name", "DBA / trade name"],
                ["website", "Website"],
                ["headquarters_city", "Headquarters city"],
                ["headquarters_region", "Headquarters region"],
                ["headquarters_country", "Headquarters country"],
                ["timezone", "Primary timezone"],
                ["currency", "Primary currency"],
                ["employee_count_range", "Employee count"],
              ] as const
            ).map(([key, label]) => (
              <Field key={key} label={label}>
                <input
                  value={value(key)}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, [key]: event.target.value }))
                  }
                />
              </Field>
            ))}
          </div>
          <div className="small muted">
            Organization ID <span className="mono">{organization.id}</span>
          </div>
        </Card>

        <div className="grid grid--two">
          <Card title="Server capabilities">
            {capabilities.loading ? (
              <Loading />
            ) : (
              <>
                <div className="grid grid--stats" style={{ marginBottom: 12 }}>
                  <Stat
                    label="Upload formats"
                    value={capabilities.data?.supported_upload_extensions.length ?? 0}
                  />
                  <Stat
                    label="Extraction providers"
                    value={capabilities.data?.extraction_providers.join(", ") ?? "—"}
                  />
                </div>
                <div className="small muted">
                  API base <span className="mono">{API_BASE_URL}</span>
                </div>
                <div className="small muted">
                  Formats: {(capabilities.data?.supported_upload_extensions ?? []).join(" ")}
                </div>
                <div className="small muted">
                  Row-structured categories:{" "}
                  {(capabilities.data?.row_structured_categories ?? []).join(", ")}
                </div>
              </>
            )}
          </Card>

          <Card
            title="Question library"
            actions={
              <button
                onClick={async () => {
                  await questionLibrary.sync();
                  validation.reload();
                }}
              >
                Reload from disk
              </button>
            }
          >
            {validation.loading ? (
              <Loading />
            ) : (
              <>
                <div className="grid grid--stats" style={{ marginBottom: 12 }}>
                  <Stat label="Modules" value={validation.data?.module_count ?? 0} />
                  <Stat
                    label="Status"
                    value={validation.data?.valid ? "valid" : "invalid"}
                    tone={validation.data?.valid ? "ok" : "danger"}
                  />
                </div>
                {validation.data?.errors.length ? (
                  <ul className="small" style={{ color: "var(--danger)" }}>
                    {validation.data.errors.slice(0, 10).map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="small muted" style={{ marginBottom: 0 }}>
                    Every module definition validated: semantic IDs, question types, page
                    references, dependencies and knowledge targets.
                  </p>
                )}
              </>
            )}
          </Card>
        </div>

        <EnterpriseContextCard
          organizationId={organizationId}
          canRefresh={can(organizationId, "registry:write")}
          canLink={can(organizationId, "org:write")}
        />

        <Card title="Controlled vocabularies">
          {lookups.loading ? (
            <Loading />
          ) : (
            <div className="grid grid--two">
              {Object.entries(lookups.data ?? {}).map(([name, rows]) => (
                <div key={name}>
                  <h3>{name.replace(/_/g, " ")}</h3>
                  <div className="row" style={{ gap: 4, marginTop: 6 }}>
                    {rows.map((row) => (
                      <span key={row.code} className="badge badge--neutral" title={row.description ?? ""}>
                        {row.label}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
